import { ChangeDetectionStrategy, Component, computed, contentChild, DestroyRef, effect, ElementRef, inject, input, model, output, signal, untracked, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { tap, timer } from 'rxjs';
import { AppContext } from 'src/app/app/context';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';
import { debounced } from '../utils/debounced';
import { logger } from '../utils/logger';
import { VisualDataSource } from './data';
import { Visual } from './Visual';
import { VisualContentError } from './VisualContentError';
import { VisualHost, VisualRowCountState } from './VisualHost';
import { CrossFilterGroup, DataPoint, DataPointCollection, DataPointSet } from './VisualInteractions';
import { VisualShellComponent } from './VisualShell.component';

const LOGGER = logger('pbir-visual-host');

@Component({
  selector: 'pbir-visual-host',
  templateUrl: './VisualHost.component.html',
  styles: `
    pbir-visual-host {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;

      &.pbir-visual-host__content_error {
        pbir-visual-shell {
          display: none !important;
          pointer-events: none !important;
          user-select: none !important;
        }
      }
    }

    pbir-visual-host .content-error-wrapper {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 2;
    }
    `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.pbir-visual-host]': 'true',
    '[class.pbir-visual-host__content_error]': '!!contentError()',
  },
  standalone: false
})
export class VisualHostComponent implements VisualHost {
  readonly hostElementRef = <ElementRef<HTMLElement>>inject(ElementRef);
  readonly destroyRef = inject(DestroyRef);
  readonly appContext = inject(AppContext);

  readonly busy = input<boolean>(false);
  readonly visual = input.required<Visual>();
  readonly dataSource = input.required<VisualDataSource>();

  readonly shell = computed(() => {
    return this.contentShell() ?? this.childShell();
  });

  readonly contentShell = contentChild(VisualShellComponent);
  readonly childShell = viewChild(VisualShellComponent);

  readonly component = computed(() => this.shell()?.component());

  readonly contentError = signal<VisualContentError>(null);

  private _className: string;

  private readonly ___updateClassList = effect(() => {
    let classList = this.hostElementRef.nativeElement.classList;

    let type = this.visual().type;

    if (this._className)
      classList.remove(this._className);

    this._className = `pbi-visual-host__${type.className}`;

    classList.add(this._className);
  });


  get disableAnimations() { return isCanvasRendererHost; }
  
  readonly renderState = model<VisualHost.RenderState>({
    rendered: false,
    rendering: false,
    startTimeMs: 0,
    endTimeMs: 0,
    durationMs: 0,
  });

  readonly broadcastRenderComplete = debounced(({ timeMs, failed, error }) => {
    let renderState = this.renderState();
    
    if (timeMs < renderState.startTimeMs)
      return;

    let durationMs = timeMs - renderState.startTimeMs;
    let duration = moment.duration(durationMs, 'ms').humanize();
    LOGGER.debug(`Render completed in ${duration} (${(durationMs / 1000.0).toFixed(3)}s)`, { host: this, renderState });

    this.renderState.set({
      ...renderState,
      rendered: true,
      rendering: false,
      endTimeMs: timeMs,
      durationMs,
      failed,
      error,
    });
  }, 50, this.destroyRef);
  

  readonly isRendered = computed(() => {
    let renderState = this.renderState();
    return !!renderState.rendered;
  });

  readonly rendered = output<VisualHost.RenderState>();

  readonly emitRendered = effect(() => {
    if (this.isRendered()) {
      this.rendered.emit(this.renderState());
    }
  });


  readonly rowCount = signal<VisualRowCountState>({
    loaded: false,
    rowCount: null,
    exceededRowLimit: false,
    error: null
  });
  readonly rowCountChange = output<VisualRowCountState>();

  readonly emitRowCountChange = effect(() => {
    this.rowCountChange.emit(this.rowCount());
  });


  readonly dataPoints = new DataPointCollection(this);
  readonly dataPointSelection = new DataPointSet();

  readonly nonDataPointSelectionActive = signal(false);
  readonly hasSelection = computed(() => this.nonDataPointSelectionActive() || this.dataPointSelection.size() > 0);
  
  selectionSet(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger) {
    untracked(() => {
      let selectionChange = false;

      if (this.dataPointSelection.set(dataPoints)) {
        this.broadcastSelectionChangeEvent(trigger);

        if (!this.dataPointSelection.size())
          this.broadcastSelectionClearedEvent(trigger);
      }
    });
  }

  selectionAdd(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger) {
    untracked(() => {
      let anyAdded = false;

      for (let p of dataPoints) {
        if (this.dataPointSelection.add(p))
          anyAdded = true;
      }

      if (anyAdded)
        this.broadcastSelectionChangeEvent(trigger);
    });
  }

  selectionRemove(dataPoints: ReadonlyArray<DataPoint>, trigger: VisualHost.EventTrigger) {
    untracked(() => {
      let anyRemoved = false;
      
      for (let p of dataPoints) {
        if (this.dataPointSelection.delete(p))
          anyRemoved = true;
      }

      if (anyRemoved) {
        this.broadcastSelectionChangeEvent(trigger);
        
        if (!this.dataPointSelection.size())
          this.broadcastSelectionClearedEvent(trigger);
      }
    });
  }

  selectionClear(trigger: VisualHost.EventTrigger) {
    untracked(() => {
      if (this.hasSelection() || this.dataPointSelection.size() > 0)
        this.selectionSet([], trigger);
    });
  }
  
  setNonDataPointSelectionActive(value: boolean, trigger: VisualHost.EventTrigger) {
    untracked(() => {
      if (value === this.nonDataPointSelectionActive())
        return;

      this.nonDataPointSelectionActive.set(value);
      this.broadcastEvent({
        type: VisualHost.EventType.NonDataPointSelectionChangeEvent,
        trigger,
        active: value
      });
    });
  }

  readonly crossFilterGroups = signal<ReadonlyArray<CrossFilterGroup>>([]);

  onBecomeCrossFilterSource(trigger: VisualHost.EventTrigger) {
    untracked(() => {
      this.broadcastEvent({
        type: VisualHost.EventType.CrossFilterBecomeSource,
        trigger,
      });
    });
  }
  
  onBecomeCrossFilterTarget(trigger: VisualHost.EventTrigger) {
    untracked(() => {
      this.broadcastEvent({
        type: VisualHost.EventType.CrossFilterBecomeTarget,
        trigger,
      });
    });
  }


  private broadcastSelectionChangeEvent(trigger: VisualHost.EventTrigger) {
    untracked(() => {
      this.broadcastEvent({
        type: VisualHost.EventType.SelectionChangeEvent,
        trigger,
        dataPoints: [...this.dataPointSelection.values()]
      });
    });
  }

  private broadcastSelectionClearedEvent(trigger: VisualHost.EventTrigger) {
    untracked(() => {
      this.broadcastEvent({
        type: VisualHost.EventType.SelectionClearedEvent,
        trigger,
      });
    });
  }
  

  readonly stateChanged = output<void>();
  

  readonly beforeVisualEvent = output<VisualHost.Event>();
  readonly visualEvent = output<VisualHost.Event>();
  private _eventDepth = 0;

  broadcastEvent<T extends VisualHost.EventBase>(e: T, options?: { noThrow?: boolean; immediate?: boolean }): void {
    this._broadcastEventImpl(e, options);
  }

  private _broadcastEventImpl<T extends VisualHost.EventBase>(e: T, options?: { noThrow?: boolean }): void {
    untracked(() => {
      let eventBaseDetails = {
        host: this,
        shell: this.shell(),
        component: this.component(),
        visual: this.visual(),
        timeMs: performance.now(),
        date: new Date(),
      };

      this._eventDepth++;

      try {
        let noThrow = !!options?.noThrow;

        try {
          let event = {
            ...eventBaseDetails,
            ...e
          };

          this._broadcastEvent(event);
        }
        catch (err) {
          if (!noThrow)
            throw err;

          LOGGER.error(`Unhandled error when broadcasting '${e.type}' event`, e, err);
        }
      }
      finally {
        this._eventDepth--;
      }
    });
  }

  private _logEvent<T extends VisualHost.Event>(event: T): void {
    let visualIdentifier = '' + (event.visual?.context.visualData()?.name ?? event.visual?.type?.name);
    let maxLength = 24;

    if (visualIdentifier.length >= maxLength)
      visualIdentifier = `${visualIdentifier.substring(0, maxLength - 6)}...${visualIdentifier.substring(visualIdentifier.length - 3, 3)}`;

    LOGGER.debug(`${event.type}: ${visualIdentifier}`, event);
  }

  private _broadcastEvent<T extends VisualHost.Event>(event: T): void {
    this._logEvent(event);

    this.beforeVisualEvent.emit(event);

    switch (event.type) {
      case VisualHost.EventType.RenderStart: {
        this.contentError.set(null);

        let current = this.renderState();
        if (current?.rendering && !(current?.rendered || current?.failed)) {
          // Ignore renderStart, if already started and not complete
          return;
        }

        this.renderState.set({
          ...this.renderState(),
          rendering: true,
          startTimeMs: current ? Math.min(current.startTimeMs, event.timeMs) : event.timeMs,
          failed: false,
          error: null,
        });
      } break;

      case VisualHost.EventType.RenderComplete: {
        this.broadcastRenderComplete({
          timeMs: event.timeMs,
          failed: !!event.failed,
          error: event.error,
        });

        if (event.error instanceof VisualContentError) {
          this.contentError.set(event.error);
        }
      } break;

      case VisualHost.EventType.RowCountLoadingEvent: {
        this.rowCount.set({
          loaded: false,
          rowCount: 0,
          exceededRowLimit: null,
          error: null
        });
      } break;

      case VisualHost.EventType.RowCountLoadedEvent: {
        this.rowCount.set({
          loaded: true,
          rowCount: event.rowCount,
          exceededRowLimit: event.exceededRowLimit,
          error: null
        });
      } break;

      case VisualHost.EventType.RowCountErrorEvent: {
        this.rowCount.set({
          loaded: true,
          rowCount: 0,
          exceededRowLimit: null,
          error: event.error
        });
      } break;

      case VisualHost.EventType.StateChangeEvent: {
        this.stateChanged.emit();
      } break;

      case VisualHost.EventType.ContentErrorChangeEvent: {
        this.contentError.set(event.error);
      } break;
    }

    this.visualEvent.emit(event);

    this.component()?.onEvent(<any>event);
  }


  getFormatter() {
    return this.component()?.formatter();
  }
}
